import { Button, Divider } from "@mui/material";
import { models } from "types/api/viewModels";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
	TruckStopOrdersCardHeader,
	TruckStopOrdersCardSubheader,
	TruckStopOrdersCardWrapper,
	TruckStopOrdersNoOrders
} from "./TruckStopOrdersCard.styles";
import {
	AddOrdersToViewText,
	AddOrderText,
	OrdersText,
	OrderText,
	RemoveStopText,
	StopNumberText,
	UpdateTruckStopText,
	YouDoNotHaveAnyOrdersText
} from "constants/text";
import { useMemo } from "react";
import TruckOrdersTable from "../TruckOrdersTable/TruckOrdersTable";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import TruckStopOrdersCardSkeleton from "./TruckStopOrdersCardSkeleton";
import { combinedJobNameAndPO } from "utils/order";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";

interface TruckStopOrdersProps {
	stop: models["TruckStopViewModel"];
	index: number;
	isLoading?: boolean;
	searchValue?: string | null;
}

const TruckStopOrdersCard = ({ stop, index, isLoading, searchValue }: TruckStopOrdersProps) => {
	const selectedAccount = useSelector((state: RootState) => state.truck.selectedAccount);
	const { openDialog } = useDialog();

	const filteredDraftOrders = useMemo(
		() =>
			stop.draftOrders?.filter((order) =>
				combinedJobNameAndPO(order)
					.toLowerCase()
					.includes(searchValue?.toLowerCase() ?? "")
			),
		[stop.draftOrders, searchValue]
	);

	return (
		<TruckStopOrdersCardWrapper data-testid="truck-stop-orders-card">
			{isLoading ? (
				<TruckStopOrdersCardSkeleton />
			) : (
				<>
					<TruckStopOrdersCardHeader>
						<div>
							<span>
								{StopNumberText}
								{index + 1}
							</span>
							<span>{`${stop.address?.name}, ${stop.address?.line1}, ${stop.address?.city}, ${stop.address?.state} ${stop.address?.zip}`}</span>
						</div>
						<div>
							<Button
								variant="outlined"
								startIcon={
									<EditIcon
										fontSize="small"
										sx={{ marginBottom: "2px" }}
									/>
								}
								onClick={() => {
									openDialog({ dialogStatus: DialogTracking.ADD_TRUCK_STOP, dialogIndex: index });
								}}
							>
								{UpdateTruckStopText}
							</Button>
							<Button
								variant="text"
								startIcon={
									<DeleteIcon
										fontSize="small"
										sx={{ marginBottom: "4px" }}
									/>
								}
								onClick={() => {
									openDialog({
										dialogStatus: DialogTracking.REMOVE_TRUCK_STOP,
										dialogIndex: index
									});
								}}
							>
								{RemoveStopText}
							</Button>
						</div>
					</TruckStopOrdersCardHeader>
					<Divider />
					<TruckStopOrdersCardSubheader>
						<span data-testid="stop-draft-orders-count">
							{filteredDraftOrders?.length} {filteredDraftOrders?.length === 1 ? OrderText : OrdersText}
						</span>
						<div>
							<Button
								variant="text"
								startIcon={
									<AddIcon
										fontSize="small"
										sx={{ marginBottom: "2px" }}
										data-testid="add-order-to-truck"
									/>
								}
								onClick={() => {
									openDialog({ dialogStatus: DialogTracking.ADD_TRUCK_ORDER, dialogIndex: index });
								}}
							>
								{AddOrderText}
							</Button>
						</div>
					</TruckStopOrdersCardSubheader>
					{filteredDraftOrders?.length ? (
						<TruckOrdersTable
							drafts={filteredDraftOrders ?? []}
							billTos={selectedAccount?.billTos ?? []}
						/>
					) : (
						<TruckStopOrdersNoOrders>
							<h6>{YouDoNotHaveAnyOrdersText}</h6>
							<span>{AddOrdersToViewText}</span>
						</TruckStopOrdersNoOrders>
					)}
				</>
			)}
		</TruckStopOrdersCardWrapper>
	);
};

export default TruckStopOrdersCard;
